<!--
  新クロージングページ左側の「本日サマリー」バー。
  クロージング対象営業日の主要な収支を表示する。
  - 売上 / 現金売上 / カード売上 : FinanceApi の income_detail（完了ステップと同じ取り方）
  - 仕入合計（経費）            : FinanceApi の spending_detail.expenseAmount
  - 社交日払い / 男子日払い      : FinanceApi の day_payment_detail（ロール別の先払い）
  - 入金額                      : 売上 − 仕入合計 − 日払い合計
-->
<template>
  <div class="eod-summary-bar">
    <div class="eod-summary-bar__title">
      本日サマリー
    </div>

    <!-- 入金額以外は横並びのグリッドで表示 -->
    <!-- 収入（売上/現金/カード）→ 区切り線 → 支出（仕入合計/社交/男子） -->
    <div class="eod-summary-bar__grid">
      <div
        v-for="row in incomeRows"
        :key="row.key"
        class="eod-summary-bar__item"
      >
        <div class="eod-summary-bar__label">
          {{ row.label }}
        </div>
        <div class="eod-summary-bar__value">
          <v-skeleton-loader
            v-if="loading"
            type="text"
            class="eod-summary-bar__skeleton"
          />
          <template v-else>{{ yen(row.value) }}</template>
        </div>
      </div>

      <!-- 収入と支出の境界線（縦1列なら カード売上↔仕入合計、3列なら 1行目↔2行目） -->
      <div class="eod-summary-bar__row-divider" />

      <div
        v-for="row in spendingRows"
        :key="row.key"
        class="eod-summary-bar__item"
      >
        <div class="eod-summary-bar__label">
          {{ row.label }}
        </div>
        <div class="eod-summary-bar__value">
          <v-skeleton-loader
            v-if="loading"
            type="text"
            class="eod-summary-bar__skeleton"
          />
          <template v-else>{{ yen(row.value) }}</template>
        </div>
      </div>
    </div>

    <!-- 入金額は全幅で強調表示 -->
    <div
      v-if="depositRow"
      class="eod-summary-bar__item eod-summary-bar__item--deposit"
    >
      <div class="eod-summary-bar__label">
        {{ depositRow.label }}
      </div>
      <div class="eod-summary-bar__value eod-summary-bar__value--accent">
        <v-skeleton-loader
          v-if="loading"
          type="text"
          class="eod-summary-bar__skeleton eod-summary-bar__skeleton--deposit"
        />
        <template v-else>{{ yen(depositRow.value) }}</template>
      </div>
      <div
        v-if="depositRow.note"
        class="eod-summary-bar__note"
      >
        {{ depositRow.note }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref, reactive, computed, watch, onMounted,
} from '@vue/composition-api'
import FinanceApi from '@/api/admin/Finance'

export default {
  name: 'EodClosingSummaryBar',
  props: {
    // 対象営業日 (YYYY-MM-DD)
    date: {
      type: String,
      default: '',
    },
    // 親がインクリメントすると再取得するトリガー。
    // セル保存・一括集計・経費/データセット変更・画面復帰などの契機で更新するために使う。
    refreshKey: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const loading = ref(false)
    const data = reactive({
      cash: 0,
      card: 0,
      receivable: 0,
      expense: 0,
      castDayPayment: 0,
      waiterDayPayment: 0,
    })

    const load = async () => {
      if (!props.date) return
      loading.value = true
      try {
        const params = { date: props.date, interval: 0, endDate: '' }
        const [income, spending, dayPayment] = await Promise.all([
          FinanceApi.getIncomeDetail(params),
          FinanceApi.getSpendingDetail(params),
          FinanceApi.getDayPaymentDetail(params),
        ])
        data.cash = Number(income?.data?.cashAmount || 0)
        data.card = Number(income?.data?.cardAmount || 0)
        data.receivable = Number(income?.data?.balanceReceivableAmount || 0)
        data.expense = Number(spending?.data?.expenseAmount || 0)
        data.castDayPayment = Number(dayPayment?.data?.castDayPaymentAmount || 0)
        data.waiterDayPayment = Number(dayPayment?.data?.waiterDayPaymentAmount || 0)
      } catch (e) {
        // サマリーは付加表示のため、取得失敗時は 0 のままにする。
      } finally {
        loading.value = false
      }
    }

    onMounted(load)
    watch(() => props.date, load)
    // refreshKey の変化で再取得（値が変わる操作の後に親がインクリメントする）。
    watch(() => props.refreshKey, load)

    const sales = computed(() => data.cash + data.card + data.receivable)
    const dayPaymentTotal = computed(() => data.castDayPayment + data.waiterDayPayment)
    const deposit = computed(() => sales.value - data.expense - dayPaymentTotal.value)

    const yen = n => `¥${Number(n || 0).toLocaleString()}`

    // 収入グループ（売上 / 現金売上 / カード売上）。
    const incomeRows = computed(() => [
      { key: 'sales', label: '売上', value: sales.value },
      { key: 'cash', label: '現金売上', value: data.cash },
      { key: 'card', label: 'カード売上', value: data.card },
    ])

    // 支出グループ（仕入合計 / 社交日払い / 男子日払い）。
    const spendingRows = computed(() => [
      { key: 'expense', label: '仕入合計', value: data.expense },
      { key: 'castDayPayment', label: '社交日払い', value: data.castDayPayment },
      { key: 'waiterDayPayment', label: '男子日払い', value: data.waiterDayPayment },
    ])

    // 入金額（全幅で強調表示）。
    const depositRow = computed(() => ({
      key: 'deposit',
      label: '入金額',
      value: deposit.value,
      note: '売上 − 仕入 − 日払い',
    }))

    return {
      loading,
      incomeRows,
      spendingRows,
      depositRow,
      yen,
    }
  },
}
</script>

<style lang="scss" scoped>
.eod-summary-bar {
  padding: 16px 18px;
}

.eod-summary-bar__title {
  margin-bottom: 18px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .04em;
  opacity: .6;
}

// 既定（デスクトップのサイドバー）は縦 1 列。行間は広めに。
.eod-summary-bar__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

// タブレット・モバイル（オーバーレイ）は横並び 3 列。
// 並び順: 売上 / 現金売上 / カード売上 → 仕入合計 / 社交日払い / 男子日払い
@media (max-width: 960px) {
  .eod-summary-bar__grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 18px 14px;
  }
}

.eod-summary-bar__item {
  min-width: 0;
}

// 収入グループと支出グループの境界線（全幅）。
.eod-summary-bar__row-divider {
  grid-column: 1 / -1;
  height: 0;
  border-top: 1px solid rgba(255, 255, 255, .08);
}

// 入金額は全幅で、上に区切り線をつけて強調。
.eod-summary-bar__item--deposit {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, .08);
}

.eod-summary-bar__label {
  margin-bottom: 4px;
  font-size: 12px;
  opacity: .55;
}

.eod-summary-bar__value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.eod-summary-bar__value--accent {
  color: var(--v-success-base);
}

.eod-summary-bar__note {
  margin-top: 2px;
  font-size: 11px;
  opacity: .4;
}

// 更新中（再取得）の値スケルトン。値の高さ(22px相当)に合わせた1本のバー。
.eod-summary-bar__skeleton {
  ::v-deep .v-skeleton-loader__text {
    height: 18px;
    margin: 3px 0;
    width: 80%;
    max-width: 110px;
    border-radius: 4px;
  }

  &--deposit ::v-deep .v-skeleton-loader__text {
    height: 22px;
    width: 60%;
    max-width: 160px;
  }
}
</style>
